import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Collapse from "@material-ui/core/Collapse";
import MenuItem from "@material-ui/core/MenuItem";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import HistoryIcon from "@material-ui/icons/History";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { canManageTenants } from "../../utils/tenant";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";
import { MainContainer, PageHeader, StatusBadge, EmptyState } from "../../components/ui";
import { getTokens } from "../../styles/tokens";

const ACTIONS = ["create", "update", "delete", "login", "logout", "other"];
const ENTITIES = [
  "company",
  "plan",
  "user",
  "queue",
  "whatsapp",
  "setting",
  "queueIntegration",
  "contact",
  "contactList",
  "quickMessage",
  "campaign",
  "tag",
  "schedule",
  "announcement",
  "invoice",
  "subscription",
  "ticketNote",
  "ticket",
  "session",
  "isp",
  "other",
];

const ACTION_TONE = {
  create: "success",
  update: "info",
  delete: "error",
  login: "neutral",
  logout: "neutral",
  other: "neutral",
};

const statusTone = (code) => {
  if (!code) return "neutral";
  if (code < 300) return "success";
  if (code < 500) return "warning";
  return "error";
};

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      flex: "none",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    filterField: {
      flex: "1 1 160px",
      maxWidth: 260,
      [theme.breakpoints.down("xs")]: { flex: "1 1 100%", maxWidth: "none" },
    },
    dateField: {
      flex: "0 0 150px",
      [theme.breakpoints.down("xs")]: { flex: "1 1 calc(50% - 6px)" },
    },
    filterButtons: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    // cartão da tabela: a lista rola por dentro e a paginação fica presa no rodapé
    tablePaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: tk.shadow.card,
    },
    tableWrap: {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 960,
      "& th": { whiteSpace: "nowrap" },
    },
    row: {
      cursor: "pointer",
      "& > td": { borderBottom: "unset" },
    },
    nowrap: { whiteSpace: "nowrap" },
    mono: {
      fontFamily: MONO,
      fontSize: 12,
      wordBreak: "break-all",
    },
    muted: { color: tk.textSecondary, fontSize: 12 },
    detail: {
      padding: "14px 20px 18px",
      backgroundColor: tk.muted,
      borderTop: `1px solid ${tk.lineRow}`,
    },
    detailGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    detailItem: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    },
    detailLabel: {
      fontSize: 12.5,
      fontWeight: 600,
      color: tk.textSecondary,
    },
    detailValue: {
      fontSize: 13.5,
      color: tk.text,
      wordBreak: "break-word",
    },
    pre: {
      margin: 0,
      padding: 12,
      borderRadius: tk.radius.md,
      border: `1px solid ${tk.line}`,
      backgroundColor: tk.paper,
      color: tk.text,
      fontFamily: MONO,
      fontSize: 12,
      maxHeight: 360,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      ...theme.scrollbarStyles,
    },
    pagination: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderTop: `1px solid ${tk.line}`,
      gap: 8,
      flexWrap: "wrap",
    },
    paginationInfo: {
      fontSize: 13,
      color: tk.textSecondary,
      fontVariantNumeric: "tabular-nums",
    },
    paginationActions: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
  };
});

const t = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const AuditLogRow = ({ log }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;
  const company = (log.company && log.company.name) || (log.companyId ? `#${log.companyId}` : "—");

  return (
    <>
      <TableRow hover className={classes.row} onClick={() => setOpen((o) => !o)}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={t("auditLogs.table.details", "Detalhes")} aria-expanded={open}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className={classes.nowrap}>{moment(log.createdAt).format("DD/MM/YYYY HH:mm:ss")}</TableCell>
        <TableCell>
          <div>{log.actorEmail || "—"}</div>
          {log.actorUserId ? <div className={classes.muted}>#{log.actorUserId}</div> : null}
        </TableCell>
        <TableCell>{company}</TableCell>
        <TableCell>
          <StatusBadge color={ACTION_TONE[log.action] || "neutral"} size="sm" label={t(`auditLogs.actions.${log.action}`, log.action)} />
        </TableCell>
        <TableCell>
          <div>{t(`auditLogs.entities.${log.entity}`, log.entity)}</div>
          {log.entityId ? <div className={classes.muted}>#{log.entityId}</div> : null}
        </TableCell>
        <TableCell className={classes.mono}>
          {log.method} {log.path}
        </TableCell>
        <TableCell>
          <StatusBadge color={statusTone(log.statusCode)} size="sm" dot={false} label={String(log.statusCode || "—")} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div className={classes.detail}>
              <div className={classes.detailGrid}>
                <div className={classes.detailItem}>
                  <span className={classes.detailLabel}>IP</span>
                  <span className={classes.detailValue}>{log.ip || "—"}</span>
                </div>
                <div className={classes.detailItem}>
                  <span className={classes.detailLabel}>{t("auditLogs.table.actorCompany", "Empresa do usuário")}</span>
                  <span className={classes.detailValue}>{log.actorCompanyId ? `#${log.actorCompanyId}` : "—"}</span>
                </div>
                <div className={classes.detailItem}>
                  <span className={classes.detailLabel}>User-Agent</span>
                  <span className={classes.detailValue} style={{ wordBreak: "break-all" }}>{log.userAgent || "—"}</span>
                </div>
              </div>
              <div className={classes.detailItem} style={{ marginTop: 16 }}>
                <span className={classes.detailLabel}>{t("auditLogs.table.changes", "Dados enviados (mascarados)")}</span>
                {hasChanges ? (
                  <pre className={classes.pre}>{JSON.stringify(log.changes, null, 2)}</pre>
                ) : (
                  <span className={classes.muted}>{t("auditLogs.table.noChanges", "Sem corpo na requisição.")}</span>
                )}
              </div>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const initialFilters = { companyId: "", entity: "", action: "", actorEmail: "", dateFrom: "", dateTo: "" };

/** Filtros + tabela. Usado pela página /audit-logs e pela aba "Auditoria" das Configurações. */
export const AuditLogsTable = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const manageTenants = useMemo(() => canManageTenants(user), [user]);

  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [pageNumber, setPageNumber] = useState(1);
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!manageTenants) return;
    let active = true;
    api
      .get("/companies/list")
      .then(({ data }) => active && setCompanies(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [manageTenants]);

  useEffect(() => {
    let active = true;
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = { pageNumber };
        Object.keys(applied).forEach((k) => {
          if (applied[k] !== "" && applied[k] !== null && applied[k] !== undefined) params[k] = applied[k];
        });
        const { data } = await api.get("/audit-logs", { params });
        if (!active) return;
        setLogs(data.records || []);
        setCount(data.count || 0);
        setHasMore(Boolean(data.hasMore));
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchLogs();
    return () => {
      active = false;
    };
  }, [applied, pageNumber, reloadTick]);

  const handleChange = (field) => (e) => setFilters((f) => ({ ...f, [field]: e.target.value }));

  const handleApply = useCallback(
    (e) => {
      if (e && e.preventDefault) e.preventDefault();
      setPageNumber(1);
      setApplied(filters);
    },
    [filters]
  );

  const handleClear = () => {
    setFilters(initialFilters);
    setApplied(initialFilters);
    setPageNumber(1);
  };

  const pageSize = 50;
  const from = count === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const to = Math.min(count, (pageNumber - 1) * pageSize + logs.length);

  return (
    <>
      <form onSubmit={handleApply} className={classes.filters}>
        {manageTenants ? (
          <TextField
            select
            id="audit-company"
            className={classes.filterField}
            size="small"
            variant="outlined"
            label={t("auditLogs.filters.company", "Empresa")}
            value={filters.companyId}
            onChange={handleChange("companyId")}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{t("auditLogs.filters.all", "Todas")}</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          select
          id="audit-entity"
          className={classes.filterField}
          size="small"
          variant="outlined"
          label={t("auditLogs.filters.entity", "Entidade")}
          value={filters.entity}
          onChange={handleChange("entity")}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">{t("auditLogs.filters.all", "Todas")}</MenuItem>
          {ENTITIES.map((e) => (
            <MenuItem key={e} value={e}>
              {t(`auditLogs.entities.${e}`, e)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          id="audit-action"
          className={classes.filterField}
          size="small"
          variant="outlined"
          label={t("auditLogs.filters.action", "Ação")}
          value={filters.action}
          onChange={handleChange("action")}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">{t("auditLogs.filters.all", "Todas")}</MenuItem>
          {ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {t(`auditLogs.actions.${a}`, a)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.filterField}
          size="small"
          variant="outlined"
          label={t("auditLogs.filters.actorEmail", "E-mail do usuário")}
          value={filters.actorEmail}
          onChange={handleChange("actorEmail")}
        />
        <TextField
          className={classes.dateField}
          size="small"
          variant="outlined"
          type="date"
          label={t("auditLogs.filters.dateFrom", "De")}
          InputLabelProps={{ shrink: true }}
          value={filters.dateFrom}
          onChange={handleChange("dateFrom")}
        />
        <TextField
          className={classes.dateField}
          size="small"
          variant="outlined"
          type="date"
          label={t("auditLogs.filters.dateTo", "Até")}
          InputLabelProps={{ shrink: true }}
          value={filters.dateTo}
          onChange={handleChange("dateTo")}
        />
        <div className={classes.filterButtons}>
          <Button type="submit" variant="outlined">
            {t("auditLogs.filters.apply", "Filtrar")}
          </Button>
          <Button onClick={handleClear}>{t("auditLogs.filters.clear", "Limpar")}</Button>
        </div>
      </form>

      <Paper className={classes.tablePaper} variant="outlined">
        {!loading && logs.length === 0 ? (
          <EmptyState
            icon={<HistoryIcon />}
            title={t("auditLogs.empty.title", "Nenhum registro encontrado")}
            message={t("auditLogs.empty.message", "Ajuste os filtros ou aguarde novas ações no sistema.")}
          />
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small" stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t("auditLogs.table.date", "Data/hora")}</TableCell>
                  <TableCell>{t("auditLogs.table.actor", "Quem")}</TableCell>
                  <TableCell>{t("auditLogs.table.company", "Empresa")}</TableCell>
                  <TableCell>{t("auditLogs.table.action", "Ação")}</TableCell>
                  <TableCell>{t("auditLogs.table.entity", "Entidade")}</TableCell>
                  <TableCell>{t("auditLogs.table.route", "Rota")}</TableCell>
                  <TableCell>{t("auditLogs.table.status", "Status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? <TableRowSkeleton columns={8} /> : logs.map((log) => <AuditLogRow key={log.id} log={log} />)}
              </TableBody>
            </Table>
          </div>
        )}
        <div className={classes.pagination}>
          <span className={classes.paginationInfo}>
            {t("auditLogs.pagination.showing", "Exibindo")} {from}–{to} {t("auditLogs.pagination.of", "de")} {count}
          </span>
          <div className={classes.paginationActions}>
            <Tooltip title={t("auditLogs.filters.refresh", "Atualizar")}>
              <IconButton
                size="small"
                aria-label={t("auditLogs.filters.refresh", "Atualizar")}
                onClick={() => setReloadTick((n) => n + 1)}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" disabled={pageNumber <= 1 || loading} onClick={() => setPageNumber((p) => Math.max(1, p - 1))}>
              {t("auditLogs.pagination.prev", "Anterior")}
            </Button>
            <Button size="small" variant="outlined" disabled={!hasMore || loading} onClick={() => setPageNumber((p) => p + 1)}>
              {t("auditLogs.pagination.next", "Próxima")}
            </Button>
          </div>
        </div>
      </Paper>
    </>
  );
};

const AuditLogs = () => {
  const { user } = useContext(AuthContext);
  // em tela estreita os filtros empilham: a página inteira rola, em vez de espremer a tabela
  const compact = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  if (!user || (user.profile !== "admin" && !user.super)) {
    return <ForbiddenPage />;
  }
  return (
    <MainContainer maxWidth scroll={compact}>
      <PageHeader
        title={t("auditLogs.title", "Auditoria")}
        subtitle={t("auditLogs.subtitle", "Quem fez o quê, quando e em qual empresa")}
      />
      <AuditLogsTable />
    </MainContainer>
  );
};

export default AuditLogs;
